// schema
import * as ISchema from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";

// plan-status-refetch imports
import { IContainer, IProps, IState } from "./PlanStatusRefetch.d";
import { planStatusRefetchFragments, query } from "./PlanStatusRefetch.gql";

// state
import { IReduxState } from "@reducers/root.reducer";
import { setFeatureGridStates } from "@actions/feature-grid.actions";

// util
import { getOrgAccountingPeriodFromOrg } from "@util/pricing.util";
import { getFeatureGridFromOrg } from "@util/feature-grid.util";
import { PostMessageManager } from "@util/post-message.util";

///////////////
// Component //
///////////////
class PlanStatusRefetchCmpt extends React.Component<IProps, IState> {
  private refetchInterval: ReturnType<typeof setInterval>;

  constructor(props: IProps) {
    super(props);
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    this.setRefetchInterval();

    // set feature grid in redux
    this.maybeSetFeatureGridStates(this.props);

    // freemium org status
    this.maybeSetFreemiumOrgStatusFromProps(this.props);
  }

  public componentWillUnmount() {
    this.clearRefetchInterval();
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    // update feature grid in redux
    this.maybeSetFeatureGridStates(nextProps);

    // update freemium org status
    this.maybeSetFreemiumOrgStatusFromProps(nextProps);
  }

  // ~-~-~-~-~-~-~-
  // Refetch
  // ~-~-~-~-~-~-~-
  private refetch = () => {
    this.props.relay.refetch({ orgRelayId: this.props.org.id }, undefined, undefined, {
      force: true,
    });
  };

  private clearRefetchInterval = () => {
    if (this.refetchInterval) {
      clearInterval(this.refetchInterval);
    }
  };

  private setRefetchInterval = () => {
    this.clearRefetchInterval();
    this.refetchInterval = setInterval(() => {
      this.refetch();
    }, 2 * 60 * 60 * 1000);
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private maybeSetFeatureGridStates = (props: IProps) => {
    const featureGrid = getFeatureGridFromOrg(props.org);

    if (featureGrid) {
      this.props.setFeatureGridStates(featureGrid.featureStates);
    }
  };

  private maybeSetFreemiumOrgStatusFromProps = (props: IProps) => {
    const { org, quotaStatusBanner } = props;
    const orgAccountingPeriod = getOrgAccountingPeriodFromOrg(org);

    if (!orgAccountingPeriod || quotaStatusBanner === ISchema.QuotaStatusBannerEnum.NoEnforcement) {
      const d = new Date(2012, 1, 1).toISOString();
      return PostMessageManager.sendFreemiumOrgStatus(
        ISchema.QuotaStatusBannerEnum.NoEnforcement,
        d,
        "",
        "",
      );
    }

    const content =
      quotaStatusBanner === ISchema.QuotaStatusBannerEnum.ReachedLimit
        ? "⚠️ Free quota exceeded. Upgrade to restore your Focus Time holds and Autopilot."
        : "Free plan near quota. Upgrade to keep your Focus Time holds and Autopilot.";
    const cta = "View Plans";

    PostMessageManager.sendFreemiumOrgStatus(
      quotaStatusBanner,
      orgAccountingPeriod.periodRange.minDate,
      content,
      cta,
    );
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    return <></>;
  }
}

///////////
// Redux //
///////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    quotaStatusBanner: state.featureGrid.mixedFreemiumOrgUIState.quotaStatusBanner,
  };
}

const PlanStatusRefetchRedux = connect(mapStateToProps, {
  setFeatureGridStates,
})(PlanStatusRefetchCmpt);

///////////
// Relay //
///////////
export const PlanStatusRefetch = createRefetchContainer<IContainer>(
  PlanStatusRefetchRedux,
  planStatusRefetchFragments,
  query,
);
